import React, { useContext, useReducer, useEffect, useRef } from 'react'
import Link from 'next/link'
import Head from 'next/head'
import { useQuery } from '@apollo/client'
import { PAGE_LAYOUT } from 'utils/pageLayoutUtils'
import { useRouter } from 'next/router'
import useAnalyticsPageTracker from 'hooks/useAnalyticsPageTracker'
import Cookies from 'js-cookie'
import GET_STARTED_QUERY from 'components/api/queries/GetStartedQuery.gql'
import { AppContext } from 'contexts/AppContext'
import {
  hasUserTypeInUrl,
  userInputReducer,
  isCCA,
  getNextStepIndex,
  getStepIndexFromUrlSlug,
  getStepRedirect,
  GET_STARTED_RETURN_URL_COOKIES_KEY,
} from 'utils/getStartedUtils'
import config from './config'
import { Container, StepPanel, LogoLink, BadgeImage, StepProgressBar } from './styles'
import ContentLoadingIndicator from 'components/generic/ContentLoadingIndicator'
import { ACTIONS, DEFAULT_USER_INPUT, GET_STARTED_ROUTES, DEFAULT_STEP } from './constants'

const GetStartedPage = () => {
  const { domain } = useContext(AppContext)
  const { steps } = config
  const router = useRouter()

  const returnUrl = router.query.return_url
  if (returnUrl) {
    Cookies.set(GET_STARTED_RETURN_URL_COOKIES_KEY, returnUrl)
  }

  const currentPageIndex = getStepIndexFromUrlSlug(steps, router?.query?.step || DEFAULT_STEP)
  const { loading, error, data, refetch } = useQuery(GET_STARTED_QUERY)
  const [userInput, dispatch] = useReducer(userInputReducer, DEFAULT_USER_INPUT)
  const previousStep = useRef()
  const currentStep = steps[currentPageIndex]
  const CurrentStepPage = currentStep?.component ?? null
  const isDataReady = !loading && !error && data
  const totalSteps = Object.keys(GET_STARTED_ROUTES).length - 1
  const stepProgressWidth = Math.ceil((currentPageIndex / totalSteps) * 100)
  const hasBadgeImage = !!data?.site?.get_started?.background?.imageUrl && !isCCA(domain)

  const onStepComplete = (submittedUserInput) => {
    if (submittedUserInput) {
      dispatch(submittedUserInput)
    }
    const nextStepIndex = getNextStepIndex({
      steps,
      currentPageIndex,
      data,
      userInput: submittedUserInput,
    })

    const nextStep = steps[nextStepIndex]
    router.push('/get-started/[step]', `/get-started/${nextStep?.urlSlug}`)
  }

  useAnalyticsPageTracker(`get-started`, router?.asPath)

  useEffect(() => {
    if (data) {
      dispatch({ type: ACTIONS.UPDATE_USER_INPUT_FROM_DATA, data })
    }
  }, [data])

  useEffect(() => {
    if (hasUserTypeInUrl({ query: router.query })) {
      const userType = router.query.user_type
      dispatch({ type: ACTIONS.UPDATE_USER_TYPE_FROM_QUERY, userType })
    }
  }, [router.query])

  /**
   * Validate that the current step can be accessed
   * If not redirect to correct route based on redirectIf rules
   */
  useEffect(() => {
    const hasPageChanged = previousStep?.current?.urlSlug !== currentStep?.urlSlug
    if (data && hasPageChanged) {
      const { redirectPath, isUrlAbsolute } = getStepRedirect({
        redirectIfTests: currentStep?.redirectIf ?? [],
        userInput,
        query: router.query,
        data,
      })
      if (redirectPath) {
        isUrlAbsolute
          ? router.replace(redirectPath)
          : router.replace('/get-started/[step]', `/get-started/${redirectPath}`)
      }
      previousStep.current = currentStep
    }
  }, [currentStep, userInput, router, data])

  return (
    <Container backgroundImage={data?.site?.get_started?.background?.imageUrl}>
      <Head>
        <title>{data?.site?.page_title}</title>
      </Head>
      <StepProgressBar style={{ width: `${stepProgressWidth}%` }} />
      <StepPanel>
        <ContentLoadingIndicator isLoading={!isDataReady}>
          {hasBadgeImage && <BadgeImage src={data?.site?.get_started?.badge?.imageUrl} />}
          {CurrentStepPage && (
            <CurrentStepPage
              data={data}
              userInput={userInput}
              saveUserInput={dispatch}
              onStepComplete={onStepComplete}
              refetchData={refetch}
            />
          )}
        </ContentLoadingIndicator>
      </StepPanel>
      {isDataReady && (
        <Link href='/'>
          <LogoLink>
            <img src={data?.site?.get_started?.logo?.imageUrl} alt='homepage' />
          </LogoLink>
        </Link>
      )}
    </Container>
  )
}

GetStartedPage.layout = PAGE_LAYOUT.CONTENT_ONLY

export default GetStartedPage
